import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const brand = params.get("brand") || "";
  const [q, setQ] = useState(params.get("q") || "");
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/products/brands").then((r) => setBrands(r.data.brands));
  }, []);

  useEffect(() => {
    setLoading(true);
    const p = {};
    if (brand) p.brand = brand;
    if (q) p.q = q;
    api.get("/products", { params: p }).then((r) => setProducts(r.data)).finally(() => setLoading(false));
  }, [brand, q]);

  const setBrand = (b) => {
    const next = new URLSearchParams(params);
    if (b) next.set("brand", b); else next.delete("brand");
    setParams(next);
  };

  const setSearchQ = (val) => {
    setQ(val);
    const next = new URLSearchParams(params);
    if (val) next.set("q", val); else next.delete("q");
    setParams(next);
  };

  const filteredBrands = useMemo(() => ["All", ...brands], [brands]);

  return (
    <div data-testid="shop-page" className="mx-auto max-w-7xl px-4 sm:px-6 py-10">
      <div className="mb-8">
        <p className="mono-label text-blue-400 mb-2">Catalog</p>
        <h1 className="font-display text-3xl sm:text-4xl tracking-tight">All mobile displays</h1>
        <p className="text-white/60 mt-2 text-sm">Filter by brand or search by model number.</p>
      </div>

      <div className="grid gap-4 md:grid-cols-[240px,1fr]">
        {/* Sidebar */}
        <aside className="border border-white/10 bg-zinc-950 p-5 h-fit sticky top-20">
          <p className="mono-label text-white/50 mb-3">Brand</p>
          <div className="space-y-1">
            {filteredBrands.map((b) => {
              const isActive = (b === "All" && !brand) || b === brand;
              return (
                <button
                  key={b}
                  data-testid={`filter-brand-${b}`}
                  onClick={() => setBrand(b === "All" ? "" : b)}
                  className={`w-full text-left px-3 py-2 text-sm border transition-colors ${
                    isActive ? "bg-blue-600/20 border-blue-500/60 text-blue-300" : "border-transparent hover:border-white/10 text-white/70 hover:text-white"
                  }`}
                >
                  {b}
                </button>
              );
            })}
          </div>
        </aside>

        <div>
          <div className="relative mb-5">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40"/>
            <Input
              data-testid="shop-search-input"
              value={q}
              onChange={(e) => setSearchQ(e.target.value)}
              placeholder="Search iPhone 11, Galaxy S21, Redmi..."
              className="pl-9 bg-zinc-950 border-white/10 focus-visible:ring-blue-600"
            />
          </div>

          {loading ? (
            <div className="text-white/50 text-sm">Loading...</div>
          ) : products.length === 0 ? (
            <div data-testid="shop-empty" className="border border-white/10 bg-zinc-950 p-10 text-center text-white/60">
              No products found. Try clearing the filters.
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
              {products.map((p) => (
                <div key={p.id} className="bg-zinc-950">
                  <ProductCard p={p} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
